/* eslint-disable */
import { getOversightUsers } from '@/api/oversight';
import { OversightUsers } from '@/api/oversight/types';
import { Search } from '@/components/Search';
import { SkeletonWrapper } from '@/components/SkeletonWrapper';
import { Info, SearchOff } from '@mui/icons-material';
import {
  Box,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableHeadData } from '../../components/Table';
import {
  StyledTableCellBody,
  SubtitleCell,
} from '../../components/Table/styles';
import { RoundedGreen, TableDataHeader } from '../../styles';
import { normalizeStr } from '../../utils';
import {
  AmountOFClients,
  FilterAndSearchContainer,
  FilterContainer,
  StyledSelect,
  TableDataName,
} from '../Clients/styles';
import { administrativeUsersFilters, Filter } from './filters';
import { AdministrativeUsersTableRow } from './types';

const HeadRow = [
  { id: 1, label: 'Usuário Administrativo', key: 'name' },
  { id: 2, label: 'Última sessão', key: 'last_activity_at' },
  { id: 3, label: 'Acessos delegados', key: 'delegated_access' },
] satisfies TableHeadData<AdministrativeUsersTableRow>[];

export function AdministrativeUsers(): JSX.Element {
  const [search, setSearch] = useState(normalizeStr(''));
  const [filters, setFilters] = useState<Filter[]>(administrativeUsersFilters);
  const [filterSelected, setFilterSelected] = useState(1);
  const [isActiveFilter, setIsActiveFilter] = useState(false);
  const [usersData, setUsersData] = useState<OversightUsers[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState(0);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  const handleActivateFilter = (filterId: number, itemValue: number): void => {
    const newFilterList = filters.map((filter) =>
      filter.id === filterId
        ? {
            ...filter,
            isActive: itemValue !== 1,
            items: filter.items.map((item) => ({
              ...item,
              active: item.value === itemValue,
            })),
          }
        : filter
    );

    setIsActiveFilter(newFilterList.some((filter) => filter.isActive));
    setFilters(newFilterList);
  };

  const renderLabel = (label: string): ReactNode | string => {
    if (label.includes('Acesso')) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', padding: '0 2px' }}>
          <Typography color="secondary">{label}</Typography>
          <Tooltip
            title="
              Filtra os supervisores com ao menos um acesso delegado no estado selecionado.
              O número na coluna “Acessos delegados” reflete o total histórico do supervisor, independente do filtro aplicado.
            "
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  whiteSpace: 'wrap',
                  maxWidth: '720px',
                },
              },
            }}
          >
            <Info
              color="disabled"
              sx={{ fontSize: '16px', margin: '4px', background: 'white' }}
            />
          </Tooltip>
        </Box>
      );
    }
    return label;
  };

  const handleToGoTo = (id: number): void =>
    navigate(`administrative-users/details/${id}`);

  const getUsers = useCallback(async () => {
    try {
      const { data } = await getOversightUsers(search, page);

      if (data) {
        setUsersData(data.users);
        setRecords(data.records);
      }
    } catch (error) {
      console.log(error);
    }
  }, [page]);

  useEffect(() => {
    getUsers();
  }, [page]);

  useEffect(() => {
    const interval = setTimeout(() => {
      setIsLoading(false);
    }, 3000);
    return () => clearTimeout(interval);
  }, []);

  return (
    <TableDataHeader>
      <FilterAndSearchContainer>
        <Search
          placeholder="Buscar por nome, empresa ou email..."
          setSearchField={setSearch}
          width="423px"
        />

        {filters.map((filter) => {
          const selectedValue =
            filter.items.find((item) => item.active)?.value ??
            filter.items[0].value;

          return (
            <FilterContainer>
              <InputLabel>{renderLabel(filter.label)}</InputLabel>

              <StyledSelect
                label={filter.label}
                value={selectedValue}
                displayEmpty
                onChange={(e: SelectChangeEvent<unknown>) =>
                  handleActivateFilter(filter.id, Number(e.target.value))
                }
              >
                {filter.items.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FilterContainer>
          );
        })}
      </FilterAndSearchContainer>

      <TableDataName>Usuários Administrativos</TableDataName>
      <AmountOFClients>
        {isLoading ? (
          <SkeletonWrapper
            isLoading={isLoading}
            variant="text"
            height={28}
            width={128}
          />
        ) : (
          `${records} usuários`
        )}
      </AmountOFClients>

      <Table
        data={usersData}
        tableHeadData={HeadRow}
        onRowClick={(row) => handleToGoTo(row.id)}
        getRowId={(row) => row.id}
        emptyTableIcon={<SearchOff />}
        emptyTableDescription="Nenhum resultado encontrado"
        records={records}
        page={page}
        setPage={setPage}
        renderRow={(row) => (
          <>
            <StyledTableCellBody>
              {row.name}

              <SubtitleCell>
                <span>{row.role}</span>
              </SubtitleCell>
            </StyledTableCellBody>
            <StyledTableCellBody>
              {row.last_activity_at ? (
                <div>
                  <RoundedGreen />
                  {row.last_activity_at}
                </div>
              ) : (
                row.last_activity_at
              )}
            </StyledTableCellBody>
            <StyledTableCellBody>
              {row.delegated_access ? row.delegated_access : '-'}
            </StyledTableCellBody>
          </>
        )}
      />
    </TableDataHeader>
  );
}
